import { More } from "./radarUi";
import { sgtTime as sgt } from "utils/radarDiagnostics";
import {
  RAIN_LABELS,
  RAIN_NAMES,
  RAIN_RULES,
  gaugeText,
  rainChip,
  rainEvidence,
  rainNowText,
  rainTimeText,
  settleText,
  stationText,
} from "utils/radarRain";
import { windowText } from "utils/radarStream";

// Rain (Radar analysis): rain at the node from its own readings and the
// nearest official rain gauge over the page's window (GET
// devices/<mac>/rain/): the gauge's line (its station, distance, rain in the
// last hour and today, or why there is none), the last hours' assessment, the
// events newest first - their stage, the reasons as the server gives them and
// the evidence behind them - and how the stages are judged. The timeline
// above marks the same events and the gauge's rain. `state`: useRadarRain's
// answer.
const plural = (n, word) =>
  `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
const hour = (h) => `${String(Math.round(h)).padStart(2, "0")}:00`;
const amount = (v, digits = 1) =>
  v.toLocaleString(undefined, { maximumFractionDigits: digits });

function RainEvent({ event, rules, gauge }) {
  const evidence = rainEvidence(event.evidence, { stage: event.stage, rules });
  const meta = [
    event.daytime === false ? "at night" : null,
    event.stage !== "gauge" && event.readings !== null
      ? plural(event.readings, "reading")
      : null,
    // A gauge exists but did not judge this one: the node's readings alone.
    gauge && gauge.state !== "unavailable" && !event.withGauge
      ? "without the gauge"
      : null,
  ].filter(Boolean);
  return (
    <li
      className={`field-rain-event is-${event.stage}`}
      aria-label={`${RAIN_NAMES[event.stage]}, ${rainTimeText(event)}${event.settled ? "" : ", still settling"}`}
    >
      <div className="field-rain-event-head">
        <span className={`field-state-pill is-rain-${event.stage}`}>
          {RAIN_LABELS[event.stage]}
        </span>
        <b>{rainTimeText(event)}</b>
        {meta.length > 0 && (
          <span className="field-rain-meta">{meta.join(" · ")}</span>
        )}
        {!event.settled && (
          <span className="field-rain-settling">Still settling</span>
        )}
      </div>
      {event.reasons.length > 0 && (
        <ul className="field-analysis-reasons" aria-label="Why">
          {event.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      {evidence.length > 0 && (
        <More label="Evidence">
          <dl className="field-rain-evidence" aria-label="Evidence">
            {evidence.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </More>
      )}
      {!event.settled && (
        <p className="field-rain-note">
          Its stage can still rise until a reading {settleText(rules)} after its
          last one has come in.
        </p>
      )}
    </li>
  );
}

// How the stages are judged, with the rules in force.
function RainRules({ rules: r }) {
  return (
    <details className="field-explain-item field-rain-how" id="radar-rain-how">
      <summary>How the stages are judged</summary>
      <div className="field-explain-body">
        <p>
          The node's temperature and humidity sensor sits inside its enclosure:
          it lags behind the outside air and never reaches its values, and the
          sun, the cooling and the humidity cannot tell a raining cloud from a
          passing one. The nearest official rain gauge (NEA, chosen from the
          node's GNSS position) can: its rain over every 5 minutes judges the
          node's signs.
        </p>
        <ol>
          <li>
            <b>Possible</b> (by day, {hour(r.day_start_h)}-{hour(r.day_end_h)}{" "}
            SGT): the sun lost - the solar current at most{" "}
            {amount(r.sun_lost_ratio * 100, 0)} % of its high of the last{" "}
            {amount(r.window_min, 0)} min - and the enclosure at least{" "}
            {amount(r.cooling_c)} °C under its own high of that hour: the node's
            signs alone.
          </li>
          <li>
            <b>Likely</b>: as well, rain at the gauge within{" "}
            {amount(r.gauge_likely_min, 0)} min of it, or our estimate rising -
            at least {amount(r.level_rising_mm / 10)} cm nearer the radar than
            before.
          </li>
          <li>
            <b>Confirmed</b>: as well, at least {amount(r.gauge_confirm_mm)} mm
            at the gauge within {amount(r.gauge_window_min, 0)} min of it, or
            our estimate at least {amount(r.level_rise_mm / 10)} cm nearer the
            radar than before - the timeline's estimate above, never the radar's
            own reading, which flips between the water and structure echoes.
          </li>
          <li>
            <b>Gauge only</b>: rain at the gauge (at least{" "}
            {amount(r.gauge_event_mm)} mm) with no sign of it at the node -
            light rain, at night, at sunset. Rain at the gauge within{" "}
            {amount(r.gauge_likely_min, 0)} min of an event is that event's.
          </li>
        </ol>
        <p>
          Without the gauge - none known for the node, or its newest reading
          more than {amount(r.gauge_stale_min, 0)} min old - the node's readings
          alone judge, as before the gauges: the humidity up at least{" "}
          {amount(r.humidity_rise_pts)} points within{" "}
          {amount(r.humidity_window_min, 0)} min makes rain likely. The gauge
          measures rain where it stands: a shower can miss it, or the node.
        </p>
        <p>
          At night there is no sun signal, and the enclosure cooling after a
          long upload or an update looks like rain: a cooling of at least{" "}
          {amount(r.night_cooling_c)} °C starts the check, but only our estimate
          makes it an event. Without bursts or rich readings there are no
          analyses, so at night only the gauge reports rain.
        </p>
        <p>
          Readings that meet the rules within {amount(r.event_gap_min, 0)} min
          of each other are one event, its stage the highest any of them
          reached; it is still settling until a reading {settleText(r)} after
          its last one has come in.
        </p>
      </div>
    </details>
  );
}

// The gauge's line: the station, its distance, its rain in the last hour and
// today; stale or unavailable, why - and then the node's readings judge alone.
function GaugeLine({ gauge }) {
  const [words, tone] = gaugeText(gauge);
  return (
    <div
      className={`field-rain-gauge ${tone}`}
      role="group"
      aria-label="Rain gauge"
    >
      <p>
        <b>Rain gauge</b> · {words}
      </p>
      {gauge.state !== "unavailable" && (
        <p className="field-caption">
          {gauge.asOf !== null
            ? `Its newest reading ${sgt(new Date(gauge.asOf).toISOString())}`
            : "No reading yet"}
          {gauge.nearby.length > 0
            ? ` · next: ${gauge.nearby
                .map(
                  (s) =>
                    `${stationText(s)} (${s.lastHourMm === null ? "no readings" : `${amount(s.lastHourMm)} mm`} in the last hour)`,
                )
                .join(", ")}`
            : ""}
          .
        </p>
      )}
    </div>
  );
}

export default function RadarRain({ state, minutes = 1440 }) {
  const rain = state?.rain ?? null;
  const gauge = rain?.gauge ?? null;
  const gauged = gauge !== null && gauge.state !== "unavailable";
  const [chip, tone] = rain
    ? rainChip(rain)
    : state?.missing
      ? ["Not available", "is-off"]
      : state?.loaded
        ? ["Not known yet", "is-off"]
        : ["Loading", "is-off"];
  const judged = rain
    ? [
        rain.asOf !== null
          ? `Judged up to ${sgt(new Date(rain.asOf).toISOString())}`
          : "No reading with an enclosure temperature to judge",
        rain.radarAsOf !== null
          ? `our estimate up to ${sgt(new Date(rain.radarAsOf).toISOString())}`
          : "no analysis of our estimate in this window, so nothing can be confirmed",
        rain.readings !== null && rain.analyses !== null
          ? `${plural(rain.readings, "reading")} and ${rain.analyses.toLocaleString()} ${rain.analyses === 1 ? "analysis" : "analyses"} over the last ${windowText(rain.days !== null ? rain.days * 1440 : minutes)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  return (
    <section
      className="field-panel field-rain"
      aria-labelledby="radar-rain-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="radar-rain-title">Rain</h2>
          <p>
            {gauged
              ? `From the node's own readings and the rain gauge ${gauge.distanceKm !== null ? `${amount(gauge.distanceKm)} km away ` : ""}over the last ${windowText(minutes)}`
              : `From the node's own readings over the last ${windowText(minutes)}: a rough estimate, not a rain gauge`}{" "}
            · <a href="#radar-rain-how">How it is judged</a>
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {chip}
        </span>
      </div>
      <div className="field-panel-body field-rain-body">
        {state?.missing ? (
          <div className="field-notice">
            This server does not judge rain yet (GET rain/); it needs the
            updated backend.
          </div>
        ) : !rain && state?.error ? (
          <div className="field-alert" role="alert">
            The rain could not load: {state.error.message}. The page tries again
            with its refresh.
          </div>
        ) : !rain ? (
          <p className="field-caption">Loading the rain…</p>
        ) : (
          <>
            <p className={`field-rain-now ${tone}`}>{rainNowText(rain)}</p>
            {gauge && <GaugeLine gauge={gauge} />}
            {state?.error && (
              <div className="field-alert" role="alert">
                The rain could not load again ({state.error.message}): this is
                the last answer.
              </div>
            )}
            {rain.events.length ? (
              <ol
                className="field-rain-events"
                aria-label="Rain events, newest first"
              >
                {rain.events.map((e) => (
                  <RainEvent
                    key={`${e.stage}-${e.tStart}`}
                    event={e}
                    rules={rain.rules}
                    gauge={gauge}
                  />
                ))}
              </ol>
            ) : (
              <div className="field-radar-empty">
                No rain in the last {windowText(minutes)}.
              </div>
            )}
            <p className="field-caption">{judged}.</p>
          </>
        )}
        <RainRules rules={rain?.rules ?? RAIN_RULES} />
      </div>
    </section>
  );
}
